<template>
    <div class="md:border-t md:border-l md:border-r md:border-black bg-white md:pt-4">
        <div class="flex pb-6">
            <div class="w-4 pl-1 ml-1 mr-3 py-5 self-center" @click="previousDelivery">
                <img
                    v-if="hasPreviousDay"
                    class="fa-rotate-180 object-fill cursor-pointer"
                    src="@/assets/images/decoration/arrow-right-black.svg">
            </div>

            <div class="w-7/12 leading-normal px-1 pt-3 font-thin text-md sm:text-lg md:text-xl">
                <div v-if="canCreateHoliday">
                    <span v-text="$t('DELIVERY_CARD_SCHEDULE')"></span>
                    <div class="pt-2">
                        <button
                            class="btn btn--half btn--secondary text-sm sm:text-base btn--no-height"
                            @click.prevent="createHoliday">
                            {{ $t('DELIVERY_CARD_SKIP') }}
                        </button>
                    </div>
                </div>
                <div v-else-if="canCreateOptInOrder">
                    <span v-text="$t('DELIVERY_CARD_HOLIDAY')"></span>
                    <div class="pt-2">
                        <button
                            class="btn btn--half btn--secondary text-sm sm:text-base"
                            @click.prevent="createOptInOrder">
                            {{ $t('DELIVERY_CARD_SEND') }}
                        </button>
                    </div>
                </div>
                <div v-else-if="canCancelOptInOrder">
                    <span v-text="$t('DELIVERY_CARD_SCHEDULE')"></span>
                    <div class="pt-2">
                        <button
                            class="btn btn--half btn--secondary text-sm sm:text-base btn--no-height"
                            @click.prevent="cancelOptInOrder">
                            {{ $t('DELIVERY_CARD_SKIP') }}
                        </button>
                    </div>
                </div>
                <div v-else-if="isHoliday">
                    <span v-text="$t('DELIVERY_CARD_HOLIDAY')"></span>
                    <div class="pt-2">
                        <button
                            class="btn btn--half btn--secondary text-sm sm:text-base"
                            @click.prevent="cancelHoliday">
                            {{ $t('DELIVERY_CARD_SEND') }}
                        </button>
                    </div>
                </div>
            </div>

            <div class="w-3/12 text-sm sm:text-md lg:text-base">
                <div class="shadow-md text-center font-thin uppercase pt-3 pb-2 leading-none">
                    <div v-text="selectedDeliveryDayTranslated.format('ddd')"></div>
                    <div v-text="selectedDeliveryDayTranslated.format('DD')" class="text-3xl sm:text-4xl md:text-5xl md:-mb-1"></div>
                    <div v-text="selectedDeliveryDayTranslated.format('MMM')"></div>
                </div>
            </div>

            <div class="w-4 pl-1 ml-3 mr-1 py-5 self-center" @click="nextDelivery">
                <img
                    v-if="hasNextDay"
                    class="float-right cursor-pointer"
                    src="@/assets/images/decoration/arrow-right-black.svg">
            </div>
        </div>

        <div
            v-if="selectedDelivery.daysLeft <= 3"
            class="bg-red-500 flex border border-solid border-black border-l-0 border-r-0 items-center">

            <img src="@/assets/images/decoration/icon-warning-white.png" class="h-6 float-left ml-6 mr-4">
            <div v-if="selectedDelivery.daysLeft" class="font-thin text-white text-sm leading-normal pr-5 py-2">
                {{ daysLeft }} {{ $t('DELIVERY_CARD_LEFT_TO_CHANGE') }}
            </div>
            <div v-else class="font-thin text-white text-sm leading-normal pr-5 py-2">
                {{ $t('DELIVERY_CARD_LAST_CHANCE') }}
            </div>
        </div>
        <div v-else class="border-solid border-t border-black"></div>

        <div v-if="!canCreateOptInOrder && !isHoliday">
            <div v-if="!showEditForm" class="font-thin py-5 border-b border-solid border-black px-6">
                <small class="font-normal" v-text="$t('DELIVERY_CARD_DELIVERED_TO')"></small>
                <div class="no-margin leading-normal">
                    <div class="flex justify-between">
                        <p v-html="getFormattedAddress"></p>
                        <div @click="toggleEditForm" class="underline cursor-pointer"
                            v-text="$t('DELIVERY_CARD_EDIT_ADDRESS')"></div>
                    </div>
                </div>
            </div>

            <edit-delivery-address-form
                v-else
                :subscription-id="subscription.id"
                :address="address"
                :delivery-comment="subscription.delivery_comment"
                :property-type="address.property_type"
                @subscription-address-updated="refreshFetchedScheduledOrders"
                @closed="toggleEditForm">
            </edit-delivery-address-form>

            <!-- Ruin the surprise -->
            <div v-if="scheduleDate"
                 class="deliveryAddressCard__action border-b"
                 @click="handleNextDeliveryBox()">
                <p class="no-margin" v-text="$t('DELIVERY_CARD_RUIN')"></p>
                <img class="deliveryAddressCard__actionIcon" src="@/assets/images/decoration/arrow-right-black.svg">
            </div>
        </div>

        <div class="border-b border-black md:border-b-0 float-right"></div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { isInCurrentWeekIncludingFirstDay } from '@/helpers/DateHelpers';
import EditDeliveryAddressForm from '@/components/forms/addresses/EditDeliveryAddressForm';
import ScheduledOrderService from '@/services/subscriptions/Subscriptions/ScheduledOrderService';
import { getFormattedAddressForCountry } from '@/helpers/AddressHelpers';

let scheduledOrderService = new ScheduledOrderService();

export default {
    name: 'DeliveryCard',
    components: {
        EditDeliveryAddressForm,
    },
    data() {
        return {
            showEditForm: false,
        };
    },
    mounted() {
        this.$store.dispatch('cms/fetchSchedules');
    },
    computed: {
        ...mapGetters({
            addressById: 'address/getAddressById',
            fullName: 'account/getFullName',
            deliveryDays: 'scheduledOrder/getDeliveryDays',
            holidays: 'scheduledOrder/getHolidays',
            schedules: 'cms/getSchedulesForActiveSubscription',
            selectedDelivery: 'scheduledOrder/getSelectedDelivery',
            subscription: 'subscription/getActiveSubscription',
            optInDeliveryDays: 'scheduledOrder/getOptInDeliveryDays',
        }),
        address() {
            return this.subscription ? this.addressById(this.subscription.delivery_address_id) : {};
        },
        selectedDeliveryDayTranslated() {
            return this.$moment(this.selectedDelivery.date);
        },
        canCancelOptInOrder() {
            return !this.selectedDelivery.isLocked && this.hasOptInOrder;
        },
        canCreateHoliday() {
            return !this.selectedDelivery.isLocked && !this.isHoliday && !this.isOptInDelivery;
        },
        canCreateOptInOrder() {
            return !this.selectedDelivery.isLocked && !this.isHoliday && this.isOptInDelivery && !this.hasOptInOrder;
        },
        daysLeft() {
            return this.selectedDelivery.daysLeft === 1 ? `${this.selectedDelivery.daysLeft} day` : `${this.selectedDelivery.daysLeft} days`;
        },
        hasPreviousDay() {
            const firstDayInDeliveryDays = this.deliveryDays[0].date;
            return this.selectedDelivery.date !== firstDayInDeliveryDays;
        },
        hasNextDay() {
            const lastDayInDeliveryDays = this.deliveryDays.slice(-1)[0].date;
            return this.selectedDelivery.date !== lastDayInDeliveryDays;
        },
        isHoliday() {
            return this.holidays.filter(value => {
                return this.selectedDelivery.date === value.date;
            }).length > 0;
        },
        isOptInDelivery() {
            return this.optInDeliveryDays.filter(value => {
                return this.selectedDelivery.date === value.date;
            }).length > 0;
        },
        hasOptInOrder() {
            return this.optInDeliveryDays.filter(value => {
                return this.selectedDelivery.date === value.date && value.id !== null;
            }).length > 0;
        },
        scheduleDate() {
            return this.schedules.reduce((scheduleId, schedule) => {
                const selectedDeliveryDate = this.$moment(this.selectedDelivery.date);
                const deliveriesStartedDate = this.$moment(schedule.deliveries_start_at);

                if (isInCurrentWeekIncludingFirstDay(selectedDeliveryDate, deliveriesStartedDate)) {
                    return schedule.deliveries_start_at;
                }

                return scheduleId;
            }, false);
        },
        getFormattedAddress() {
            return getFormattedAddressForCountry(this.address, this.fullName);
        },
    },
    methods: {
        cancelHoliday() {
            let holiday = this.holidays.filter(element => this.selectedDelivery.date === element.date)[0];

            scheduledOrderService.cancelHoliday({
                subscriptionId: this.subscription.id,
                holidayId: holiday.id,
            }).then(() => {
                this.$emit('holiday-button-clicked');
                this.refreshFetchedScheduledOrders();
            });
        },

        cancelOptInOrder() {
            let optInDeliveryDay = this.optInDeliveryDays.filter(element => {
                return this.selectedDelivery.date === element.date;
            })[0];

            scheduledOrderService.cancelOptInOrder({
                subscriptionId: this.subscription.id,
                holidayId: optInDeliveryDay.id,
            }).then(() => {
                this.$emit('holiday-button-clicked');
                this.$emit('has-recently-skipped-delivery');
                this.refreshFetchedScheduledOrders();
            });
        },

        createHoliday() {
            scheduledOrderService.createHoliday({
                subscriptionId: this.subscription.id,
                deliveryDate: this.selectedDelivery.date,
            }).then(() => {
                this.$emit('holiday-button-clicked');
                this.$emit('has-recently-skipped-delivery');
                this.refreshFetchedScheduledOrders();
            });
        },

        createOptInOrder() {
            scheduledOrderService.createOptInOrder({
                subscriptionId: this.subscription.id,
                deliveryDate: this.selectedDelivery.date,
            }).then(() => {
                this.$emit('holiday-button-clicked');
                this.refreshFetchedScheduledOrders();
            });
        },

        nextDelivery() {
            this.$store.dispatch('scheduledOrder/selectDeliveryDay', this.selectedDelivery.day.next);
        },

        previousDelivery() {
            this.$store.dispatch('scheduledOrder/selectDeliveryDay', this.selectedDelivery.day.previous);
        },

        refreshFetchedScheduledOrders() {
            this.$store.dispatch('scheduledOrder/fetchAllScheduledOrders', true);
            this.$store.dispatch('subscription/getNextDeliveryDate', true);
        },

        toggleEditForm() {
            this.showEditForm = !this.showEditForm;
        },

        handleNextDeliveryBox() {
            this.$router.push({
                name: 'account.your-boxes.redirect',
                params: {
                    scheduleDate: this.scheduleDate,
                    subscriptionId: this.subscription.id,
                },
            });
        },
    },
};
</script>
